import React, { useState, useMemo } from "react";
import { FiChevronLeft, FiChevronRight, FiPieChart, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { ContentHeader, StatCard, PillTabs, DetailPanel, PanelSection } from "../../../components/v4";
import useProcessSpendingData from "./useProcessSpendData";
import MonthlyTotalsTable from "../../components/MonthlyTotalsTable";
import MultiBusinessTotalsTable from "../../components/MultiBusinessTotalsTable";

const DEFAULT_YEAR = 2026;

// Collapsible section component
const CollapsibleSection = ({ title, children, defaultExpanded = true }) => {
	const [expanded, setExpanded] = useState(defaultExpanded);

	return (
		<div className="v4-spending__section">
			<div className="v4-spending__section-header" onClick={() => setExpanded(!expanded)}>
				<div className={`v4-spending__section-toggle ${expanded ? "v4-spending__section-toggle--expanded" : ""}`}>
					{expanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
				</div>
				<h3 className="v4-spending__section-title">{title}</h3>
			</div>
			<div className={`v4-spending__section-content ${expanded ? "v4-spending__section-content--expanded" : ""}`}>
				<div className="v4-spending__section-inner">{children}</div>
			</div>
		</div>
	);
};

const SpendingAverages = () => {
	const currentYear = new Date().getFullYear();
	const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);
	const [tab, setTab] = useState("Home");
	const [details, setDetails] = useState(null);

	const startDate = `${selectedYear}-01-01`;
	const endDate = `${selectedYear}-12-31`;

	const { compiledData } = useProcessSpendingData({ startDate, endDate, categoriesToExclude: ["Erin Paycheck"] });

	const goToPrevYear = () => setSelectedYear((y) => y - 1);
	const goToNextYear = () => setSelectedYear((y) => y + 1);
	const goToCurrentYear = () => setSelectedYear(currentYear);

	// Calculate stats
	const stats = useMemo(() => {
		if (!compiledData || compiledData.length === 0) {
			return { totalSpent: 0, monthlyAvg: 0, monthlyHomeAvg: 0, monthlyAvgExRent: 0 };
		}

		const homeBusinesses = ["Home", "Other", "Apartment"];
		const businessBusinesses = ["NL", "Immerge", "Projects"];
		const relevantBusinesses = tab === "Home" ? homeBusinesses : businessBusinesses;

		// Total scoped to active tab's businesses
		let totalSpent = 0;
		compiledData.forEach((business) => {
			if (!relevantBusinesses.includes(business.business)) return;
			if (!business.categories) return;
			business.categories.forEach((cat) => {
				totalSpent += cat.total || 0;
			});
		});

		// Monthly average (based on data, not always 12)
		const monthsDenominator = compiledData[0]?.monthsInRange || 12;
		const monthlyAvg = totalSpent / monthsDenominator;

		// Monthly Home average (Home business only)
		let homeTotal = 0;
		const homeBiz = compiledData.find((b) => b.business === "Home");
		if (homeBiz?.categories) {
			homeBiz.categories.forEach((cat) => {
				homeTotal += cat.total || 0;
			});
		}
		const monthlyHomeAvg = homeTotal / monthsDenominator;

		// Business monthly average excluding Rent and Utilities
		const excludedCategories = ["rent", "utilities", "rent 2026"];
		let bizTotalExRent = 0;
		compiledData.forEach((business) => {
			if (!businessBusinesses.includes(business.business)) return;
			if (!business.categories) return;
			business.categories.forEach((cat) => {
				// Check group name first (e.g., Apartment's "Rent" / "Utilities" groups)
				if (excludedCategories.includes((cat.category || "").toLowerCase())) return;
				// If group has subcategories, check each one (NL/Immerge lump everything into "Other")
				if (cat.subcategories) {
					cat.subcategories.forEach((sub) => {
						if (!excludedCategories.includes((sub.category || "").toLowerCase())) {
							bizTotalExRent += sub.total || 0;
						}
					});
				} else {
					bizTotalExRent += cat.total || 0;
				}
			});
		});
		const monthlyAvgExRent = bizTotalExRent / monthsDenominator;

		return { totalSpent, monthlyAvg, monthlyHomeAvg, monthlyAvgExRent };
	}, [compiledData, tab]);

	// Calculate panel total
	const detailsTotal = useMemo(() => {
		if (!details?.entries?.length) return 0;
		return details.entries.reduce((sum, e) => {
			const amt = typeof e.transaction === "number" ? e.transaction : Number(e.transaction || 0);
			return sum + amt;
		}, 0);
	}, [details]);

	// Sort entries by amount (magnitude)
	const sortedEntries = useMemo(() => {
		if (!details?.entries?.length) return [];
		return [...details.entries].sort((a, b) => {
			const an = typeof a.transaction === "number" ? a.transaction : Number(a.transaction || 0);
			const bn = typeof b.transaction === "number" ? b.transaction : Number(b.transaction || 0);
			return Math.abs(bn) - Math.abs(an);
		});
	}, [details]);

	const formatCurrency = (val) => Math.round(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 });

	// Get business data helpers
	const getBusinessData = (businessName) => compiledData.filter((obj) => obj.business === businessName)[0];

	// Year navigation component
	const yearNavActions = (
		<nav className="v4-year-nav">
			<button className="v4-year-nav__btn" onClick={goToPrevYear}>
				<FiChevronLeft />
			</button>
			<span className="v4-year-nav__label">{selectedYear}</span>
			<button className="v4-year-nav__btn" onClick={goToNextYear}>
				<FiChevronRight />
			</button>
			{selectedYear !== currentYear && (
				<button className="v4-year-nav__today" onClick={goToCurrentYear}>
					{currentYear}
				</button>
			)}
		</nav>
	);

	return (
		<div className="v4-spending">
			<ContentHeader
				icon={<FiPieChart />}
				title="Spending Averages"
				subtitle={`${selectedYear} Overview`}
				metadata={`${tab} spending breakdown by category`}
				actions={yearNavActions}
			/>

			{/* Tab Navigation */}
			<div className="v4-spending__tabs">
				<PillTabs tabs={["Home", "Business"]} activeTab={tab} onChange={setTab} />
			</div>

			{/* Stats Row */}
			<div className="v4-stats-row">
				<StatCard label="Total Spent" value={Math.round(stats.totalSpent)} prefix="$" />
				<StatCard label="Monthly Average" value={Math.round(stats.monthlyAvg)} prefix="$" />
				{tab === "Home"
					? <StatCard label="Monthly Home Avg" value={Math.round(stats.monthlyHomeAvg)} prefix="$" />
					: <StatCard label="Avg ex Rent/Util" value={Math.round(stats.monthlyAvgExRent)} prefix="$" />
				}
			</div>

			{/* Totals */}
			<CollapsibleSection title={tab === "Home" ? "Home Totals" : "Business Totals"}>
				{tab === "Home"
					? <MultiBusinessTotalsTable selectedBusinesses={["Home", "Other", "Apartment"]} compiledData={compiledData} />
					: <MultiBusinessTotalsTable selectedBusinesses={["NL", "Immerge", "Projects"]} compiledData={compiledData} />
				}
			</CollapsibleSection>

			{/* Home Tab Content */}
			{tab === "Home" && (
				<>
					<CollapsibleSection title="Home">
						<MonthlyTotalsTable
							selectedBusiness="Home"
							businessData={getBusinessData("Home")}
							excludedCategories={[]}
							onShowDetails={setDetails}
						/>
					</CollapsibleSection>
					<CollapsibleSection title="Apartment">
						<MonthlyTotalsTable
							selectedBusiness="Apartment"
							businessData={getBusinessData("Apartment")}
							onShowDetails={setDetails}
						/>
					</CollapsibleSection>
					<CollapsibleSection title="Other">
						<MonthlyTotalsTable
							selectedBusiness="Other"
							businessData={getBusinessData("Other")}
							onShowDetails={setDetails}
						/>
					</CollapsibleSection>
				</>
			)}

			{/* Business Tab Content */}
			{tab === "Business" && (
				<>
					<CollapsibleSection title="NL">
						<MonthlyTotalsTable
							selectedBusiness="NL"
							businessData={getBusinessData("NL")}
							onShowDetails={setDetails}
						/>
					</CollapsibleSection>
					<CollapsibleSection title="Immerge">
						<MonthlyTotalsTable
							selectedBusiness="Immerge"
							businessData={getBusinessData("Immerge")}
							onShowDetails={setDetails}
						/>
					</CollapsibleSection>
					<CollapsibleSection title="Projects">
						<MonthlyTotalsTable
							selectedBusiness="Projects"
							businessData={getBusinessData("Projects")}
							onShowDetails={setDetails}
						/>
					</CollapsibleSection>
				</>
			)}

			{/* Detail Panel */}
			<DetailPanel
				isOpen={details !== null}
				onClose={() => setDetails(null)}
				title={details?.title?.split(" | ")[0] || "Details"}
				subtitle={details?.title?.split(" | ")[1] || ""}
				footer={
					<div className="v4-spending__panel-total">
						Total: {formatCurrency(detailsTotal)}
					</div>
				}
			>
				<PanelSection title="Entries">
					<div className="v4-spending-entries__list">
						{sortedEntries.map((entry, idx) => {
							const amount = typeof entry.transaction === "number" ? entry.transaction : Number(entry.transaction || 0);
							const desc = entry.description || entry.note || entry.merchant || entry.category || "";
							const dateStr = entry.date ? new Date(entry.date).toLocaleDateString("en-US") : "";

							return (
								<div className="v4-spending-entries__entry" key={idx}>
									<span className="v4-spending-entries__date">{dateStr}</span>
									<span className="v4-spending-entries__description">{desc}</span>
									<span className={`v4-spending-entries__amount ${amount < 0 ? "v4-spending-entries__amount--negative" : ""}`}>
										{formatCurrency(amount)}
									</span>
								</div>
							);
						})}
					</div>
				</PanelSection>
			</DetailPanel>
		</div>
	);
};

export default SpendingAverages;
